import React from 'react';
import ShoppingCartAssistant from './ShoppingCartAssistant';
import { webdesignedLayoutTier } from './core/resolveExperienceMode';
import { useLanguage } from '../../contexts/useLanguage';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';

/**
 * Webdesigned wrapper for the landing ShoppingCartAssistant.
 * Uses WebDesignedCartContext for open state and cart sidebar; stores shopping data for checkout.
 */
const WebDesignedCartAssistant: React.FC<{ topOffset?: number }> = ({ topOffset = 88 }) => {
  const cart = useWebDesignedCart();
  const { language, setLanguage } = useLanguage();
  const [viewportWidth, setViewportWidth] = React.useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  const layoutMode: 'phone' | 'tablet' | 'desktop' = webdesignedLayoutTier(viewportWidth);

  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!cart) return null;

  const {
    assistantOpen,
    setAssistantOpen,
    setCartSidebarOpen,
    setShoppingCartData,
    assistantMinimized,
    setAssistantMinimized,
  } = cart;

  if (!assistantOpen) return null;

  return (
    <ShoppingCartAssistant
      isOpen={true}
      onToggle={() => {
        if (assistantMinimized) {
          setAssistantMinimized(false);
          return;
        }
        setAssistantMinimized(true);
        setAssistantOpen(false);
      }}
      onContinue={(data) => setShoppingCartData(data)}
      onMinimizeChange={(minimized) => {
        setAssistantMinimized(minimized);
        if (minimized) setAssistantOpen(false);
      }}
      onCartClick={() => setCartSidebarOpen(true)}
      language={language}
      setLanguage={setLanguage as (lang: 'fr' | 'en' | 'zh') => void}
      isMinimizedProp={assistantMinimized}
      externalTravel={cart.shoppingCartData}
      topOffset={topOffset}
      transparentWhenUnhovered={false}
      layoutMode={layoutMode}
    />
  );
};

export default WebDesignedCartAssistant;
